import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  theme: 'dark',
  toggle: () => {},
  setTheme: () => {},
});

const VALID = new Set(['light', 'dark']);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem('aivoryems.ui.theme');
      return VALID.has(stored) ? stored : 'light';
    } catch {
      return 'dark';
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('light', theme === 'light');
    root.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // setTheme(value, persist=true) -- callers should pass persist=true so the
  // last-applied theme is cached in localStorage. ThemeProvider seeds from
  // that cache on next mount, so pages that re-fetch the server-side
  // setting (admin workspace.settings.org.theme, public event.theme) paint
  // in the right theme immediately without flashing the default first.
  // persist=false is left in for callers that want a transient override.
  const setTheme = (value, persist = true) => {
    if (!VALID.has(value)) return;
    setThemeState(value);
    if (persist) {
      try {
        localStorage.setItem('aivoryems.ui.theme', value);
      } catch {
        // no-op
      }
    }
  };

  const toggle = () => setTheme(theme === 'dark' ? 'light' : 'dark', true);

  return <ThemeContext.Provider value={{ theme, toggle, setTheme }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
